import * as Schema from "effect/Schema";

export const WorkspaceEnvironmentPreflightStatus = Schema.Literals([
  "ready",
  "degraded",
  "blocked",
]);
export type WorkspaceEnvironmentPreflightStatus = typeof WorkspaceEnvironmentPreflightStatus.Type;

export const WorkspaceEnvironmentConfigSource = Schema.Literals([
  "forge",
  "cursor",
  "inferred",
  "none",
]);
export type WorkspaceEnvironmentConfigSource = typeof WorkspaceEnvironmentConfigSource.Type;

export const WorkspaceEnvironmentCommand = Schema.Struct({
  command: Schema.String,
  source: WorkspaceEnvironmentConfigSource,
});
export type WorkspaceEnvironmentCommand = typeof WorkspaceEnvironmentCommand.Type;

export const WorkspaceEnvironmentTerminal = Schema.Struct({
  name: Schema.String,
  command: Schema.String,
});
export type WorkspaceEnvironmentTerminal = typeof WorkspaceEnvironmentTerminal.Type;

export const WorkspaceEnvironmentConfigIssue = Schema.Struct({
  severity: Schema.Literals(["degraded", "blocked"]),
  detail: Schema.String,
});
export type WorkspaceEnvironmentConfigIssue = typeof WorkspaceEnvironmentConfigIssue.Type;

export const WorkspaceEnvironmentConfig = Schema.Struct({
  source: WorkspaceEnvironmentConfigSource,
  path: Schema.NullOr(Schema.String),
  installCommand: Schema.NullOr(WorkspaceEnvironmentCommand),
  startCommand: Schema.NullOr(WorkspaceEnvironmentCommand),
  terminals: Schema.Array(WorkspaceEnvironmentTerminal),
  systemPackages: Schema.Array(Schema.String),
  secrets: Schema.Array(Schema.String),
  issues: Schema.Array(WorkspaceEnvironmentConfigIssue),
});
export type WorkspaceEnvironmentConfig = typeof WorkspaceEnvironmentConfig.Type;

export const WorkspaceEnvironmentPreflightCheck = Schema.Struct({
  name: Schema.String,
  status: WorkspaceEnvironmentPreflightStatus,
  detail: Schema.String,
});
export type WorkspaceEnvironmentPreflightCheck = typeof WorkspaceEnvironmentPreflightCheck.Type;

export const WorkspaceEnvironmentPreflightReport = Schema.Struct({
  status: WorkspaceEnvironmentPreflightStatus,
  cwd: Schema.String,
  packageManager: Schema.NullOr(Schema.String),
  environmentConfig: WorkspaceEnvironmentConfig,
  checks: Schema.Array(WorkspaceEnvironmentPreflightCheck),
});
export type WorkspaceEnvironmentPreflightReport = typeof WorkspaceEnvironmentPreflightReport.Type;

export const WorkspaceEnvironmentPreflightInput = Schema.Struct({
  cwd: Schema.String,
});
export type WorkspaceEnvironmentPreflightInput = typeof WorkspaceEnvironmentPreflightInput.Type;

export class WorkspaceEnvironmentError extends Schema.TaggedErrorClass<WorkspaceEnvironmentError>()(
  "WorkspaceEnvironmentError",
  {
    message: Schema.String,
  },
) {}

/**
 * Ask a runtime for a directory a thread can run in when the user has not
 * chosen a repository — the "Don't use a workspace" path.
 *
 * There is no input: the root is the runtime's to decide, and deliberately not
 * the client's. A cloud sprite roots its projects at `FORGE_CLOUD_PROJECTS_ROOT`
 * while a desktop runtime has no such variable, and `ServerConfig` publishes no
 * projects root at all — so a client-side guess would produce a project whose
 * `workspaceRoot` does not exist, which `probeProjectAvailability` then reports
 * as `missing`.
 */
export const WorkspaceEnsureScratchInput = Schema.Struct({});
export type WorkspaceEnsureScratchInput = typeof WorkspaceEnsureScratchInput.Type;

export const WorkspaceEnsureScratchResult = Schema.Struct({
  /**
   * Absolute path to a directory that exists on the runtime by the time this
   * resolves. Stable across calls, so the caller can dedupe a project against
   * it by `cwd` instead of creating one scratch project per thread.
   */
  workspaceRoot: Schema.String,
  /** Human-facing project title for the scratch workspace. */
  title: Schema.String,
});
export type WorkspaceEnsureScratchResult = typeof WorkspaceEnsureScratchResult.Type;
